import React from "react";
import { SEO } from "../components/SEO";
import { BUSINESS_INFO } from "../config";
import { ContactForm } from "../components/ContactForm";

export function ContactPage() {
  return (
    <>
      <SEO
        title="Kontakt - Hundens Pels og Pleje"
        description={`Kontakt Hundens Pels og Pleje i Fredericia. Brovadvej 56, 7000 Fredericia. Tlf: ${BUSINESS_INFO.phone}. Book tid online eller send os en besked.`}
      />

      {/* Header - No box behind heading and description */}
      <section className="py-12 sm:py-16 bg-white border-b border-[#E8DFD5]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-black">
            Kontakt Hundens Pels og Pleje
          </h1>

          <p className="mx-auto mt-3 max-w-2xl text-base sm:text-lg text-neutral-700 leading-relaxed">
            Har du spørgsmål til en behandling, din hunds pelstype eller ønsker du at booke tid? Du er altid meget velkommen til at kontakte os.
          </p>
        </div>
      </section>

      {/* Content Grid: Info & Form */}
      <section className="py-12 sm:py-16 bg-[#FEFAF3]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 items-start">
            {/* Left Col: Contact Information - STRICTLY NO BOX BEHIND CONTACT INFO */}
            <div className="lg:col-span-5 space-y-8">
              {/* Online booking highlight - clean, no icon box */}
              <div className="space-y-3">
                <h2 className="font-heading text-xl sm:text-2xl font-medium text-black">
                  Online tidsbestilling
                </h2>
                <p className="text-sm text-neutral-700 leading-relaxed">
                  I vores online bookingkalender kan du i ro og mag se alle ledige tider og bestille den behandling, der passer bedst til dig og din hund. Finder du ikke en tid, der passer, eller kan du ikke finde den ønskede tid, er du altid velkommen til at tage kontakt.
                </p>
                <div className="pt-2">
                  <a
                    href={BUSINESS_INFO.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#dabeb9] px-7 py-3.5 text-sm font-medium tracking-wide text-black shadow-xs transition-colors hover:bg-[#c9a7a0]"
                  >
                    <i className="fa-solid fa-calendar-check text-black" aria-hidden="true" />
                    <span>Gå til online booking</span>
                  </a>
                </div>
              </div>

              {/* Salonen i Fredericia information - NO BOX, NO BOXES BEHIND ICONS */}
              <div className="space-y-6 pt-6 border-t border-[#E8DFD5]">
                <h3 className="font-heading text-lg font-medium text-black">
                  Salonen i Fredericia
                </h3>

                {/* Adresse */}
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-location-dot text-xl text-black mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-black block">
                      Adresse
                    </span>
                    <p className="text-sm font-semibold text-black mt-0.5">
                      {BUSINESS_INFO.name}
                    </p>
                    <p className="text-sm text-black">
                      {BUSINESS_INFO.address}
                    </p>
                  </div>
                </div>

                {/* Telefon (Single line enforced) */}
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-phone text-xl text-black mt-0.5 shrink-0" aria-hidden="true" />
                  <div className="flex-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-black block">
                      Telefon & SMS
                    </span>
                    <div className="flex flex-wrap items-center gap-3 mt-0.5">
                      <a
                        href={`tel:${BUSINESS_INFO.phoneRaw}`}
                        className="text-base font-bold text-black hover:underline whitespace-nowrap block"
                      >
                        {BUSINESS_INFO.phone}
                      </a>
                      <a
                        href={`sms:${BUSINESS_INFO.phoneRaw}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#EAE2D7] px-3 py-1 text-xs font-bold text-black hover:bg-[#DDD3C5] transition-colors"
                      >
                        <i className="fa-solid fa-comment-sms text-xs text-black" aria-hidden="true" />
                        <span>Send SMS</span>
                      </a>
                    </div>
                    <span className="text-xs text-neutral-700 block mt-1.5">
                      <strong>Telefontid:</strong> Hverdage kl. 8.00 – 8.30
                    </span>
                    <span className="text-xs text-neutral-600 block mt-0.5">
                      Send meget gerne en SMS, hvis jeg er optaget med en hund eller uden for telefontid, så vender jeg tilbage hurtigst muligt.
                    </span>
                  </div>
                </div>

                {/* Email (Single line enforced) */}
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-envelope text-xl text-black mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-black block">
                      Email
                    </span>
                    <a
                      href={`mailto:${BUSINESS_INFO.email}`}
                      className="text-sm font-semibold text-black hover:underline whitespace-nowrap block mt-0.5"
                    >
                      {BUSINESS_INFO.email}
                    </a>
                  </div>
                </div>

                {/* Facebook link - clean, no box */}
                <div className="pt-2">
                  <a
                    href={BUSINESS_INFO.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-black hover:underline"
                  >
                    <i className="fa-brands fa-facebook text-lg text-black" aria-hidden="true" />
                    <span>Følg salonen på Facebook</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Right Col: Webform (The only subpage with a Webform) */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
